// ============================================================================
// ErrorBoundary — captura crashes de render pra não dar tela branca
// ============================================================================

import React from 'react';

// Mesmos padrões (e mesma flag de sessionStorage) do auto-recovery do main.jsx.
// Quando o React.lazy falha, o erro cai AQUI no boundary — não dispara
// window.error/unhandledrejection — então a recuperação precisa existir nos dois.
const CHUNK_RELOAD_KEY = '__chunkErrorReload';
function isChunkLoadError(msg) {
  if (!msg) return false;
  const m = String(msg).toLowerCase();
  return m.includes('importing a module script failed')
      || m.includes('failed to fetch dynamically imported module')
      || m.includes('module script failed to load')
      || m.includes('loading chunk')
      || m.includes('loading css chunk');
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] crash capturado:', error, errorInfo);
    // Chunk antigo depois de deploy: limpa caches do SW e recarrega UMA vez
    // (flag em sessionStorage evita loop se o problema for outro).
    if (isChunkLoadError(error?.message) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      this.setState({ errorInfo, recovering: true });
      const reload = () => window.location.reload();
      if ('caches' in window) {
        caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))).finally(reload);
      } else {
        reload();
      }
      return;
    }
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  copyStack = () => {
    const text = [
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Mensagem: ${this.state.error?.message || String(this.state.error)}`,
      '',
      'Stack do erro:',
      this.state.error?.stack || '(sem stack)',
      '',
      'Component stack:',
      this.state.errorInfo?.componentStack || '(sem component stack)',
    ].join('\n');
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => alert('Erro copiado! Cola aqui no chat pra eu investigar.'),
        () => prompt('Copia esse texto:', text)
      );
    } else {
      prompt('Copia esse texto:', text);
    }
  };

  render() {
    if (this.state.recovering) {
      // Atualização em andamento — nada de tela de erro assustadora
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-slate-300 border-t-ink rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">Atualizando o sistema…</p>
          </div>
        </div>
      );
    }
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      // Detecta erro #310 (hooks count mismatch) e dá dica específica
      const isHooksError = /Minified React error #(310|301)/.test(msg) || /hooks/i.test(msg);
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white border border-red-200 rounded-2xl p-6 max-w-2xl w-full shadow-lg">
            <h1 className="text-xl font-bold text-red-700 mb-2">😬 Algo quebrou</h1>
            <p className="text-sm text-slate-700 mb-4">
              A tela travou por um erro inesperado. Clica em <strong>Copiar erro</strong> e
              cola pra mim no chat — assim eu consigo achar a causa rápido.
            </p>
            {isHooksError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-800">
                <strong>Erro de hooks detectado.</strong> Me diz também: qual aba estava aberta?
                Qual botão você clicou logo antes de quebrar?
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 max-h-60 overflow-auto">
              <pre className="text-xs text-red-700 whitespace-pre-wrap font-mono">
                {msg}
                {this.state.error?.stack && '\n\n' + this.state.error.stack}
                {this.state.errorInfo?.componentStack && (
                  '\n\nComponent stack:' + this.state.errorInfo.componentStack
                )}
              </pre>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={this.copyStack}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
              >
                📋 Copiar erro
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gold text-white rounded-lg text-sm font-semibold hover:opacity-90"
              >
                Recarregar página
              </button>
              <button
                onClick={this.reset}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50"
              >
                Tentar continuar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
